import { Link } from 'react-router-dom';
import { getFeatures } from '@4cm/core/features';
import './Legal.css';

/**
 * Children's privacy notice — ICO Children's Code §4 mandates a
 * separate, age-appropriate version targeting Flesch-Kincaid grade ≤6
 * (roughly 9–12 year-old reading age). Audio TTS reading available
 * via the existing voice provider — listen instead of read.
 *
 * Source: docs/legal/privacy-for-young-players.md.
 *
 * V1 LAUNCH POSTURE (2026-05-13): the page is still reachable but
 * leads with a deferral note when `features.under13PlayerAccounts`
 * is false — under-13s can't sign up in v1, so the privacy promises
 * below describe what *will* happen once we open the cohort. See
 * docs/spec/22a-v1-launch-posture.md.
 *
 * **Reading panel test pending pre-launch** (ICO best practice).
 */
export function PrivacyForYoungPlayers() {
  const { under13PlayerAccounts } = getFeatures();

  return (
    <main className="legal-page">
      <div className="legal-page__eyebrow">For young players</div>
      <h1 className="legal-page__title">Hi! Here's what 4CM does with your stuff</h1>
      {!under13PlayerAccounts && (
        <div className="legal-page__notice">
          <strong>You can't sign up to 4CM yet if you're under 13.</strong>{' '}
          We're working on it. For now, your coach uses 4CM for your team, and
          your parent can ask the coach to share your progress with them. When
          we do open it up, this page tells you what we'll keep about you and
          what you can do about it. Keep reading if you want to see.
        </div>
      )}
      <p className="legal-page__lead">
        4CM is an app you use to get better at football with your coach. We need
        a bit of info about you to do that. This page tells you what we keep,
        why, and what you can do about it.
      </p>
      <p>
        If a word is too long, ask a parent or coach. The big version is at{' '}
        <Link to="/legal/privacy">our main privacy notice</Link> — it's for grown-ups.
      </p>

      <h2>What do you give us?</h2>
      <ul>
        <li><strong>Your name.</strong> So your coach knows who you are.</li>
        <li><strong>Your email.</strong> So we can let you in.</li>
        <li><strong>Your date of birth.</strong> So we know if you need a parent's help to use 4CM.</li>
        <li><strong>Your team and club.</strong> So you and your team-mates can see the same stuff.</li>
      </ul>
      <p>If your parent or coach asks, you can also share:</p>
      <ul>
        <li><strong>How you played.</strong> What your coach thought (a "rating") and what you thought too.</li>
        <li><strong>How you felt.</strong> Tired? Happy? Sore? You can log this if you want — it's called a "wellness log."</li>
        <li><strong>How tall and heavy you are.</strong> Only if you and your parent say yes.</li>
        <li><strong>Goals you want to hit.</strong> Like "get better at first touch" — you write these.</li>
        <li><strong>Notes on a clip.</strong> If your coach saves a video moment for you, they might add a note.</li>
      </ul>
      <p>
        You don't have to share any of this. The app still works without it.{' '}
        <strong>Don't share something just because someone asked you to</strong> —
        ask your parent first if you're not sure.
      </p>

      <h2>Who can see your stuff?</h2>
      <ul>
        <li><strong>You</strong> can see everything about you.</li>
        <li><strong>Your coach</strong> can see most things — your ratings, your goals, your wellness logs (if you said it's OK), and any clip notes you have.</li>
        <li><strong>Your parent</strong> can see what you let them see.</li>
        <li><strong>Your team-mates</strong> can see your name and when you played. They <strong>don't</strong> see your wellness log, your bio-band data, or your private notes.</li>
        <li><strong>No one else.</strong> We never sell your stuff. We never give it to ads. We never give it to a stranger.</li>
      </ul>

      <h2>What if AI helps your coach?</h2>
      <p>
        Sometimes 4CM uses an AI helper. It might write a sentence about how
        you're doing.
      </p>
      <p>
        For your account, <strong>the AI is switched OFF unless you (or your
        parent) say yes.</strong> If you turn it on:
      </p>
      <ul>
        <li>The AI looks at your ratings and goals.</li>
        <li>It writes a short note for you and your coach.</li>
        <li>Your coach reads it before you do, to make sure it's fair.</li>
        <li>You can switch it off any time.</li>
      </ul>
      <p>
        The AI doesn't decide if you make a team. It doesn't compare you to
        other players. It's just a helper.
      </p>

      <h2>You're in charge</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>See everything we have on you.</strong> Download a copy from <em>Settings → Privacy → Download my data</em>.</li>
        <li><strong>Get something fixed if it's wrong.</strong> Edit your profile, or message your coach.</li>
        <li><strong>Delete your account.</strong> <em>Settings → Privacy → Delete my account</em>.</li>
        <li><strong>Pause everything.</strong> Stops new ratings and wellness logs.</li>
        <li><strong>Say no</strong> to a kind of processing (like AI, or marketing emails).</li>
        <li><strong>Tell us if something feels wrong.</strong> Tap "Report" on whatever's bothering you. Or email <a href="mailto:welfare@4cmcoaching.co.uk">welfare@4cmcoaching.co.uk</a> — we read every one.</li>
      </ul>

      <h2>Being kind on 4CM</h2>
      <p><strong>Yes:</strong></p>
      <ul>
        <li>Be kind. Be honest. Cheer for your team-mates.</li>
        <li>Tap Report if you see something that's not OK.</li>
        <li>Talk about football.</li>
      </ul>
      <p><strong>No:</strong></p>
      <ul>
        <li>Bullying. Mean words. Nasty rating comments.</li>
        <li>Sharing your team-mates' clips or notes outside 4CM without asking them.</li>
        <li>Pretending to be a different age.</li>
      </ul>
      <p>
        If you break the rules, your coach + a Welfare Officer will help you
        understand what happened. If it's something serious, your account
        might pause for a while.
      </p>

      <h2>If you're not OK</h2>
      <p>
        Sometimes the app spots when you're feeling really low. If that happens
        we'll show you links to people who can help. <strong>Tell a grown-up
        you trust</strong> — your coach, your parent, your Welfare Officer.
      </p>
      <p>You can always:</p>
      <ul>
        <li>Call <strong>Childline</strong> for free: <strong>0800 1111</strong> (under-19, in the UK)</li>
        <li>Text <strong>YoungMinds Crisis Messenger</strong>: text <code>YM</code> to <code>85258</code> (any time)</li>
        <li>Call <strong>Samaritans</strong> for free: <strong>116 123</strong> (any age, any time)</li>
        <li>Visit your <strong>GP</strong> or call <strong>NHS 111</strong></li>
      </ul>
      <p>These are real grown-ups who listen. You're not on your own.</p>

      <h2>How to tell us anything</h2>
      <ul>
        <li>Ask a question about your data — email <a href="mailto:dpo@4cmcoaching.co.uk">dpo@4cmcoaching.co.uk</a></li>
        <li>Report something on the app — tap <strong>Report</strong></li>
        <li>Talk to a grown-up at 4CM — email <a href="mailto:welfare@4cmcoaching.co.uk">welfare@4cmcoaching.co.uk</a></li>
        <li>Tell the UK regulator (the ICO) — visit <a href="https://ico.org.uk">ico.org.uk</a></li>
      </ul>

      <h2>When this changes</h2>
      <p>
        If we change anything important about what we do with your stuff, we'll
        send you (and your parent if you're under 13) a message. You'll get to
        say yes or no to anything new.
      </p>

      <p className="legal-page__footer">
        Last updated: 2026-05-08. Reading panel scheduled before UK launch.
        For the grown-up version see <Link to="/legal/privacy">our main privacy notice</Link>.
      </p>
    </main>
  );
}
